import classNames from 'classnames'
import { useEffect, useState } from 'react'

import { t } from '../../../../../common/i18n.js'
import { Language } from '../../../../../common/types/i18n.js'
import { Select } from '../../../../components/forms/select/select.js'
import { BookOpen } from '../../../../components/icon/solar-icons.js'
import { portalGet } from '../../utils/api.js'
import { formatLedgerDate, getTypeBadgeClass } from '../../utils/format.js'

interface LedgerEntry {
    id: number
    transaction_type: string
    amount: number
    note: string | null
    created_at_utc: string
    policy_id: number
    /** TASK-567: the day an admin correction is true "as of"; null on other rows */
    effective_date: string | null
    actor_name: string | null
}

interface PolicyInfo {
    policyId: number
    policyName: string
    leaveTypeName: string
}

interface LedgerPanelProps {
    employeeId: number
    policies: PolicyInfo[]
    lang: Language
    // Controlled open state. Falls back to internal state when omitted.
    open?: boolean
    onOpenChange?: (open: boolean) => void
}

// Read-only transaction history — the audit trail of every grant, deduction,
// expiration and admin correction (who, when, as of which date, why). Balance
// corrections themselves are made from the stats card (TASK-567).
export const LedgerPanel = ({
    employeeId,
    policies,
    lang,
    open,
    onOpenChange,
}: LedgerPanelProps) => {
    const [internalOpen, setInternalOpen] = useState(false)
    const isOpen = open ?? internalOpen
    const setIsOpen = (value: boolean) => {
        onOpenChange?.(value)
        if (open === undefined) setInternalOpen(value)
    }
    const [entries, setEntries] = useState<LedgerEntry[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [filterPolicyId, setFilterPolicyId] = useState<number | ''>('')

    const tL = t.portal.timeOff.ledger

    // Transaction types are UPPER_SNAKE in the ledger; map them onto the
    // camelCase i18n keys (unknown/legacy types fall back to the raw string).
    const typeLabels: Record<string, (l: Language) => string> = {
        INITIAL_BALANCE: tL.initialBalance,
        ACCRUAL: tL.accrual,
        DEDUCTION: tL.deduction,
        EXPIRATION: tL.expiration,
        MANUAL_ADJUSTMENT: tL.manualAdjustment,
    }

    // Load ledger when opened
    useEffect(() => {
        if (!isOpen) return

        const load = async () => {
            setIsLoading(true)
            const policyParam = filterPolicyId !== '' ? `?policy_id=${filterPolicyId}` : ''
            const result = await portalGet<LedgerEntry[]>(
                `/api/portal/employees/${employeeId}/leave-ledger${policyParam}`,
            )
            if (result.ok) setEntries(result.data)
            setIsLoading(false)
        }

        void load()
    }, [isOpen, employeeId, filterPolicyId])

    // Build policy lookup from props, falling back to IDs from entries
    const policyMap = new Map(policies.map((p) => [p.policyId, p]))
    const allPolicyIds = [
        ...new Set([...policies.map((p) => p.policyId), ...entries.map((e) => e.policy_id)]),
    ]

    const getPolicyLabel = (pid: number): string => {
        const info = policyMap.get(pid)
        return info ? `${info.leaveTypeName} — ${info.policyName}` : `#${pid}`
    }

    return (
        <div className="portal-profile__ledger">
            <button
                type="button"
                className="portal-profile__ledger-toggle"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>
                    <BookOpen size={14} /> {tL.title(lang)}
                </span>
                <span className="portal-profile__ledger-chevron">{isOpen ? '▾' : '▸'}</span>
            </button>

            {isOpen && (
                <div className="portal-profile__ledger-content">
                    {allPolicyIds.length > 1 && (
                        <div className="portal-profile__ledger-toolbar">
                            <Select
                                className="portal-profile__ledger-filter"
                                value={filterPolicyId === '' ? '' : String(filterPolicyId)}
                                onChange={(value) => setFilterPolicyId(value ? Number(value) : '')}
                                options={[
                                    { value: '', label: t.portal.inline.allPolicies(lang) },
                                    ...allPolicyIds.map((pid) => ({
                                        value: String(pid),
                                        label: getPolicyLabel(pid),
                                    })),
                                ]}
                            />
                        </div>
                    )}

                    {/* Transaction table */}
                    {isLoading ? (
                        <div className="portal-profile__ledger-loading">⏳</div>
                    ) : entries.length === 0 ? (
                        <div className="portal-profile__ledger-empty">
                            {t.portal.inline.noEntries(lang)}
                        </div>
                    ) : (
                        <table className="portal-profile__ledger-table">
                            <thead>
                                <tr>
                                    <th>{tL.date(lang)}</th>
                                    <th>{tL.type(lang)}</th>
                                    <th>{tL.amount(lang)}</th>
                                    <th>{tL.effectiveDate(lang)}</th>
                                    <th>{tL.actor(lang)}</th>
                                    <th>{tL.noteLabel(lang)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((entry) => {
                                    const isPositive = Number(entry.amount) > 0

                                    return (
                                        <tr key={entry.id}>
                                            <td className="portal-profile__ledger-date">
                                                {formatLedgerDate(entry.created_at_utc, lang)}
                                            </td>
                                            <td>
                                                <span
                                                    className={classNames(
                                                        'portal-profile__ledger-badge',
                                                        getTypeBadgeClass(entry.transaction_type),
                                                    )}
                                                >
                                                    {typeLabels[entry.transaction_type]?.(lang) ??
                                                        entry.transaction_type}
                                                </span>
                                            </td>
                                            <td
                                                className={classNames(
                                                    'portal-profile__ledger-amount',
                                                    isPositive
                                                        ? 'portal-profile__ledger-amount--positive'
                                                        : 'portal-profile__ledger-amount--negative',
                                                )}
                                            >
                                                {isPositive ? '+' : ''}
                                                {entry.amount}
                                            </td>
                                            <td className="portal-profile__ledger-date">
                                                {entry.effective_date
                                                    ? formatLedgerDate(entry.effective_date, lang)
                                                    : '—'}
                                            </td>
                                            <td className="portal-profile__ledger-actor">
                                                {entry.actor_name ?? '—'}
                                            </td>
                                            <td className="portal-profile__ledger-note">
                                                {entry.note ?? '—'}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    )
}
